import React, { useId } from 'react';
import { isBlankCustomValue, isTruthyCheckboxValue, toNumberInput } from './custom-fields/fieldTypes';

const INPUT_CLASS = "w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500";

// `errors` is { [field.key]: message }, shown under the field in edit mode (D4: a
// Required custom field left empty). The caller decides when to show them.
// 0 and false are values (#972); only null / undefined / '' / [] read as empty.
export default function CustomFieldRenderer({ fields = [], values = {}, onChange, editing = false, errors = {}, className = '' }) {
    // Per instance, so two renderers on one page (a modal over a detail page) never share ids.
    const scope = useId();
    if (!fields.length) return null;

    const handleChange = (key, value) => {
        if (onChange) {
            onChange({ ...values, [key]: value });
        }
    };

    return (
        <div className={className}>
            <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Custom Fields</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-3">
                {fields.map(field => {
                    const value = values[field.key] ?? field.default_value ?? '';

                    if (!editing) {
                        // View mode
                        return (
                            <div key={field.key}>
                                <label className="block text-xs text-stone-400 mb-0.5">{field.label}</label>
                                <div className="text-sm text-stone-900">
                                    {field.field_type === 'checkbox' ? (
                                        <span className={isTruthyCheckboxValue(value) ? 'text-emerald-600' : 'text-stone-400'}>{isTruthyCheckboxValue(value) ? 'Yes' : 'No'}</span>
                                    ) : field.field_type === 'multi_select' && Array.isArray(value) ? (
                                        <div className="flex flex-wrap gap-1">
                                            {value.map(v => (
                                                <span key={v} className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">{v}</span>
                                            ))}
                                        </div>
                                    ) : field.field_type === 'url' && value ? (
                                        <a href={value} target="_blank" rel="noopener noreferrer" className="text-stone-900 underline">{value}</a>
                                    ) : (
                                        <span>{isBlankCustomValue(value) ? <span className="text-stone-300">—</span> : String(value)}</span>
                                    )}
                                </div>
                            </div>
                        );
                    }

                    // Edit mode
                    const error = errors?.[field.key];
                    const controlId = `${scope}-cf-${field.key}`;
                    const errorId = `${scope}-cf-error-${field.key}`;
                    const invalid = error ? { 'aria-invalid': true, 'aria-describedby': errorId } : {};
                    const labelText = (
                        <>
                            {field.label}
                            {field.required && <span className="text-red-500 ml-0.5">*</span>}
                        </>
                    );
                    const isChoiceGroup = field.field_type === 'checkbox' || field.field_type === 'multi_select';
                    return (
                        <div key={field.key}>
                            {/* A checkbox is named by its own wrapping label and a multi-select group
                                by this heading, so neither gets a second, competing label. */}
                            {field.field_type === 'checkbox' ? (
                                <span className="block text-xs font-medium text-stone-600 mb-1">{labelText}</span>
                            ) : (
                                <label id={`${controlId}-label`} htmlFor={isChoiceGroup ? undefined : controlId}
                                       className="block text-xs font-medium text-stone-600 mb-1">{labelText}</label>
                            )}

                            {field.field_type === 'textarea' ? (
                                <textarea
                                    id={controlId}
                                    {...invalid}
                                    value={isBlankCustomValue(value) ? '' : value}
                                    onChange={e => handleChange(field.key, e.target.value)}
                                    rows={2}
                                    className={INPUT_CLASS}
                                />
                            ) : field.field_type === 'select' ? (
                                <select
                                    id={controlId}
                                    {...invalid}
                                    value={isBlankCustomValue(value) ? '' : value}
                                    onChange={e => handleChange(field.key, e.target.value)}
                                    className={INPUT_CLASS}
                                >
                                    <option value="">Select...</option>
                                    {(field.options || []).map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            ) : field.field_type === 'multi_select' ? (
                                <div className="space-y-1" role="group" aria-labelledby={`${controlId}-label`}
                                     aria-describedby={error ? errorId : undefined}>
                                    {(field.options || []).map(opt => (
                                        <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
                                            <input
                                                type="checkbox"
                                                {...invalid}
                                                checked={(Array.isArray(value) ? value : []).includes(opt)}
                                                onChange={e => {
                                                    const arr = Array.isArray(value) ? [...value] : [];
                                                    if (e.target.checked) arr.push(opt);
                                                    else arr.splice(arr.indexOf(opt), 1);
                                                    handleChange(field.key, arr);
                                                }}
                                                className="rounded border-stone-300"
                                            />
                                            {opt}
                                        </label>
                                    ))}
                                </div>
                            ) : field.field_type === 'checkbox' ? (
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input
                                        id={controlId}
                                        type="checkbox"
                                        {...invalid}
                                        checked={isTruthyCheckboxValue(value)}
                                        onChange={e => handleChange(field.key, e.target.checked)}
                                        className="rounded border-stone-300"
                                    />
                                    {field.label}
                                </label>
                            ) : (
                                <input
                                    type={field.field_type === 'number' ? 'number' :
                                          field.field_type === 'date' ? 'date' :
                                          field.field_type === 'email' ? 'email' :
                                          field.field_type === 'phone' ? 'tel' :
                                          field.field_type === 'url' ? 'url' : 'text'}
                                    id={controlId}
                                    {...invalid}
                                    value={isBlankCustomValue(value) ? '' : value}
                                    onChange={e => handleChange(field.key, field.field_type === 'number' ? toNumberInput(e.target.value) : e.target.value)}
                                    className={INPUT_CLASS}
                                />
                            )}
                            {error && (
                                <p id={errorId} role="alert" className="mt-1 text-xs text-red-600" data-testid={`custom-field-error-${field.key}`}>{error}</p>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
